import {
    TransportObjectType,
    MinimalTransportObject,
    ExtendedTransportObject,
} from '@/types/transport-object'
import {
    MinimalResource,
    Model,
    Dispatch,
    uuid,
    ChecklistField,
    LinkedChecklistField,
} from '@/types/general'
import { IncludedCompany, Location, LocationPoint } from '@/types/company'
import { MinimalUser } from '@/types/user'
import { Alarm } from '@/types/alarm'

export type ReportFilter = 'all' | 'withAlarms' | 'withoutAlarms' | 'draft'

export enum ReportState {
    Draft = 0,
    Confirmed = 1,
}

export interface Report extends Model {
    id: uuid
    user: MinimalUser
    company: IncludedCompany
    companyLocation: Location
    customerCompany: IncludedCompany | null
    haulierCompany: IncludedCompany | null
    haulierLocation: Location | null
    transportObject: ExtendedTransportObject
    template: Template
    fields: ExtendedReportField[]
    tour: MinimalResource | null
    vehicle?: MinimalTransportObject
    reason: ReportReason | null
    type: ReportType
    location: LocationPoint
    address: string
    note: string | null
    notes: ReportNote[]
    wheels: number[][] | null
    createdAt: string
    updatedAt: string
    transpiredAt: string
    departure: string | null
    lastReportId: uuid | null
    nextReportId: uuid | null
    currentReportPosition: number
    totalReports: number
    alarms: ReportAlarm[]
    dispatches: Dispatch[]
    isAutomaticDropOff: boolean
    state: ReportState
}

export interface MinimalReport extends Model {
    id: uuid
    user: MinimalUser
    company: IncludedCompany
    companyLocation: Location
    customerCompany: IncludedCompany | null
    haulierCompany: IncludedCompany | null
    haulierLocation: Location | null
    transportObject: MinimalTransportObject
    vehicle: MinimalTransportObject | null
    template: { id: uuid; name: string } | null
    reason: ReportReason | null
    location: LocationPoint
    address: string
    type: ReportType
    note: string
    wheels: number[][] | null
    hasNewIncidents: boolean
    hasTireThresholdAlarm: boolean
    hasFieldAlarm: boolean
    createdAt: string
    updatedAt: string
    transpiredAt: string
    departure: string | null
    isAutomaticDropOff: boolean
    state: ReportState
}

export interface MinimalTourReport {
    id: uuid
    type: ReportType
    transportObject: { id: uuid; type: TransportObjectType; registrationNumber: string }
}

export enum ReportReason {
    LoadingShip = 0,
    UnLoadingShip = 1,
    Terminal = 2,
    PTI = 3,
}

export interface TransitReport extends Model {
    id: uuid
    company: IncludedCompany
    haulierCompany: IncludedCompany | null
    location: LocationPoint
    user: MinimalUser
    transpiredAt: string
    categories: TransitReportCategory[]
}

export interface MinimalTransitReport extends Model {
    id: uuid
    company: IncludedCompany
    haulierCompany: IncludedCompany | null
    transpiredAt: string
    categoryTypes: TransitCategoryType[]
}

export interface TransitReportCategory extends Model {
    id: uuid
    type: TransitCategoryType
    note: string | null
}

export interface MasterTemplate {
    id: string
    name: string
    axles: number
    type: TransportObjectType
    images?: MasterTemplateImage[]
    primaryImage?: MasterTemplateImage
}

export interface MasterTemplateImage {
    id: uuid
    masterTemplateId: string
    name: string
    path: string
    primaryImage: boolean
    companyLogoAngle: string
    sections: MasterTemplateSection[]
}

export interface Template {
    id: uuid
    companyId: uuid
    masterTemplate: MasterTemplate
    name: string
    externalTemplateId: string | null
    tireThreadCheck: boolean
    tireThreadThreshold?: number
    fields: ChecklistField[]
}

export interface WithSections {
    sections: MasterTemplateSection[]
}

export interface MasterTemplateSection {
    id: uuid
    coordinates: string
    name: string
}

export interface ReportField {
    id: uuid
    reportId: uuid
    checklistFieldId: uuid
    value: string
    notPossibleToCheck: boolean
}

export interface ExtendedReportField extends ReportField {
    checklistField: LinkedChecklistField
    lastReportValue: string | null
    lastReportNotPossibleToCheck: boolean | null
    images: ReportFieldImage[]
}

export enum AlarmType {
    Below = 0,
    Above = 1,
    Equals = 2,
    Filled = 3,
}

export enum ReportType {
    Pickup = 0,
    DropOff = 1,
}

export enum TransitCategoryType {
    DamageOwnTruckTrailer = 0,
    DamageGoods = 1,
    DamageOtherMaterials = 2,
    DamageCounterPart = 3,
}

export interface CompanyLogoAngle {
    coordinates: string
    matrix: string
}

export interface ReportFieldImage {
    id: uuid
    reportFieldId: uuid
    path: string
}

export interface ReportNote {
    id: uuid
    reportId: uuid
    masterTemplateSectionId: uuid
    value: string
}

export enum ReportPage {
    Incidents = 0,
    TireThreads = 1,
    Fields = 2,
}

export enum VehicleReportPreference {
    Required = 0,
    Optional = 1,
    Off = 2,
}

export interface DamageReportPreferences {
    sendReportsToOwners: boolean
    requireCustomerCompany: boolean
    tourSelectable: boolean
    createVehiclesInApp: boolean
    createUnitsInApp: boolean
    requireIncidentNote: boolean
}

export interface ReportAlarm extends Pick<
    Alarm,
    'id' | 'type' | 'comment' | 'ticketNumber' | 'resolvedAt' | 'mutedAt' | 'description'
> {
    description: ReportAlarmDescription
}

interface ReportAlarmDescription extends Record<string, unknown> {
    incidentId?: uuid
}
